
import { supabase } from '@/integrations/supabase/client';
import { getCourseQuizScores, calculateCourseQuizAverage } from '@/hooks/auth/progressService';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

export interface CertificateData {
  id: string;
  userId: string;
  courseId: string;
  courseTitle: string;
  certificateNumber: string;
  userName: string;
  quizAverage: number;
  completionDate: string;
  verificationHash?: string;
}

export interface MarkSheetData {
  userName: string;
  courseTitle: string;
  quizScores: Array<{
    moduleName: string;
    percentage: number;
    passed: boolean;
    attemptDate: string;
    score: number;
    maxScore: number;
  }>;
  overallAverage: number;
  certificateNumber: string;
  completionDate: string;
}

// Helper function to safely extract metadata
function extractMetadata(metadata: any): { quiz_average?: number; [key: string]: any } {
  if (typeof metadata === 'object' && metadata !== null) {
    return metadata as { quiz_average?: number; [key: string]: any };
  }
  return {};
}

class CertificateService {
  // Generate certificate number
  static generateCertificateNumber(courseId: string): string {
    const timestamp = Date.now();
    const random = Math.random().toString(36).substring(2, 8).toUpperCase();
    return `CERT-${courseId.toUpperCase()}-${timestamp}-${random}`;
  }

  // Generate verification hash
  static async generateVerificationHash(data: string): Promise<string> {
    const encoder = new TextEncoder();
    const dataBuffer = encoder.encode(data);
    const hashBuffer = await crypto.subtle.digest('SHA-256', dataBuffer);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  }

  // Store certificate in database
  static async storeCertificate(
    userId: string,
    courseId: string,
    courseTitle: string,
    userName: string,
    quizAverage: number
  ): Promise<CertificateData | null> {
    try {
      const certificateNumber = this.generateCertificateNumber(courseId);
      const verificationData = `${userId}-${courseId}-${certificateNumber}-${Date.now()}`;
      const verificationHash = await this.generateVerificationHash(verificationData);

      const { data, error } = await supabase
        .from('certificates')
        .insert({
          user_id: userId,
          course_id: courseId,
          course_title: courseTitle,
          certificate_number: certificateNumber,
          user_name: userName,
          verification_hash: verificationHash,
          metadata: {
            quiz_average: quizAverage,
            generated_at: new Date().toISOString(),
            version: '2.0'
          }
        })
        .select()
        .single();

      if (error) throw error;

      return {
        id: data.id,
        userId: data.user_id,
        courseId: data.course_id,
        courseTitle: data.course_title,
        certificateNumber: data.certificate_number,
        userName: data.user_name,
        quizAverage,
        completionDate: new Date(data.issue_date).toLocaleDateString(),
        verificationHash: data.verification_hash
      };
    } catch (error) {
      console.error('Error storing certificate:', error);
      return null;
    }
  }

  // Get certificate data
  static async getCertificate(userId: string, courseId: string): Promise<CertificateData | null> {
    try {
      const { data, error } = await supabase
        .from('certificates')
        .select('*')
        .eq('user_id', userId)
        .eq('course_id', courseId)
        .single();

      if (error || !data) return null;

      const metadata = extractMetadata(data.metadata);

      return {
        id: data.id,
        userId: data.user_id,
        courseId: data.course_id,
        courseTitle: data.course_title,
        certificateNumber: data.certificate_number,
        userName: data.user_name,
        quizAverage: metadata.quiz_average || 0,
        completionDate: new Date(data.issue_date).toLocaleDateString(),
        verificationHash: data.verification_hash
      };
    } catch (error) {
      console.error('Error fetching certificate:', error);
      return null;
    }
  }

  // Get mark sheet data
  static async getMarkSheetData(userId: string, courseId: string, courseTitle: string): Promise<MarkSheetData | null> {
    try {
      // Get certificate for basic info
      const certificate = await this.getCertificate(userId, courseId);
      if (!certificate) return null;

      // Get detailed quiz scores
      const quizScores = await getCourseQuizScores(userId, courseId);
      const quizData = await calculateCourseQuizAverage(userId, courseId);

      return {
        userName: certificate.userName,
        courseTitle: certificate.courseTitle,
        quizScores: quizScores.map(score => ({
          moduleName: score.moduleName,
          percentage: score.percentage,
          passed: score.passed,
          attemptDate: score.attemptDate,
          score: score.score,
          maxScore: score.maxScore
        })),
        overallAverage: quizData.overallAverage,
        certificateNumber: certificate.certificateNumber,
        completionDate: certificate.completionDate
      };
    } catch (error) {
      console.error('Error fetching mark sheet data:', error);
      return null;
    }
  }

  // Generate and download PDF directly using html2canvas + jsPDF
  static async generatePDF(elementId: string, filename: string): Promise<void> {
    try {
      const element = document.getElementById(elementId) as HTMLElement | null;
      if (!element) {
        console.error(`Certificate element with ID "${elementId}" not found in DOM`);
        throw new Error(`Certificate not found. Please refresh the page and try again.`);
      }

      // Store original parent and sibling for restoration
      const originalParent = element.parentNode;
      const originalNextSibling = element.nextSibling;

      // Store original styles of the certificate element
      const originalStyles = {
        display: element.style.display,
        position: element.style.position,
        top: element.style.top,
        left: element.style.left,
        zIndex: element.style.zIndex,
        width: element.style.width,
        height: element.style.height,
        overflow: element.style.overflow,
      };

      try {
        // Temporarily move certificate to body root and make visible
        document.body.appendChild(element);
        
        element.style.display = 'block';
        element.style.position = 'relative';
        element.style.top = '0';
        element.style.left = '0';
        element.style.width = 'auto';
        element.style.height = 'auto';
        element.style.zIndex = '9999';
        element.style.overflow = 'visible';

        // Wait for fonts to load
        if ((document as any).fonts?.ready) {
          await (document as any).fonts.ready;
        }
        
        // Small delay to ensure layout is stable
        await new Promise(resolve => setTimeout(resolve, 100));

        // Capture the element as a high-resolution canvas
        const canvas = await html2canvas(element, {
          scale: 2,
          useCORS: true,
          backgroundColor: '#ffffff',
          logging: false,
          windowWidth: element.scrollWidth,
          windowHeight: element.scrollHeight,
        });

        const imgData = canvas.toDataURL('image/png');

        // Create PDF in landscape A4 format
        const pdf = new jsPDF({
          orientation: 'landscape',
          unit: 'mm',
          format: 'a4',
        });

        const pageWidth = pdf.internal.pageSize.getWidth();
        const pageHeight = pdf.internal.pageSize.getHeight();

        // Calculate dimensions to fit the image on the page
        const imgWidthPx = canvas.width;
        const imgHeightPx = canvas.height;
        const ratio = Math.min(pageWidth / imgWidthPx, pageHeight / imgHeightPx);

        const imgWidth = imgWidthPx * ratio;
        const imgHeight = imgHeightPx * ratio;

        // Center the image on the page
        const x = (pageWidth - imgWidth) / 2;
        const y = (pageHeight - imgHeight) / 2;

        pdf.addImage(imgData, 'PNG', x, y, imgWidth, imgHeight);

        // Ensure filename has .pdf extension
        const safeFilename = filename.toLowerCase().endsWith('.pdf')
          ? filename
          : `${filename}.pdf`;

        // Download the PDF
        pdf.save(safeFilename);

      } finally {
        // Restore original styles
        Object.assign(element.style, originalStyles);

        // Move certificate back to its original parent
        if (originalParent) {
          originalParent.insertBefore(element, originalNextSibling);
        }
      }

    } catch (error) {
      console.error('Error generating PDF:', error);
      throw error;
    }
  }

  // Verify certificate
  static async verifyCertificate(certificateNumber: string): Promise<CertificateData | null> {
    try {
      const { data, error } = await supabase
        .from('certificates')
        .select('*')
        .eq('certificate_number', certificateNumber)
        .single();

      if (error || !data) return null;

      const metadata = extractMetadata(data.metadata);

      return {
        id: data.id,
        userId: data.user_id,
        courseId: data.course_id,
        courseTitle: data.course_title,
        certificateNumber: data.certificate_number,
        userName: data.user_name,
        quizAverage: metadata.quiz_average || 0,
        completionDate: new Date(data.issue_date).toLocaleDateString(),
        verificationHash: data.verification_hash
      };
    } catch (error) {
      console.error('Error verifying certificate:', error);
      return null;
    }
  }

  // Get user certificates
  static async getUserCertificates(userId: string): Promise<CertificateData[]> {
    try {
      const { data, error } = await supabase
        .from('certificates')
        .select('*')
        .eq('user_id', userId)
        .order('issue_date', { ascending: false });

      if (error) throw error;

      return (data || []).map(cert => {
        const metadata = extractMetadata(cert.metadata);
        
        return {
          id: cert.id,
          userId: cert.user_id,
          courseId: cert.course_id,
          courseTitle: cert.course_title,
          certificateNumber: cert.certificate_number,
          userName: cert.user_name,
          quizAverage: metadata.quiz_average || 0,
          completionDate: new Date(cert.issue_date).toLocaleDateString(),
          verificationHash: cert.verification_hash
        };
      });
    } catch (error) {
      console.error('Error fetching user certificates:', error);
      return [];
    }
  }
}

export default CertificateService;
